import { ClientsScreen } from '@cortex/feature-client-management/ui/admin/screens/ClientsScreen';
import { FleetScreen } from '@cortex/feature-client-management/ui/admin/screens/FleetScreen';
import { FormOnboardingScreen } from '@cortex/feature-client-management/ui/admin/screens/FormOnboardingScreen';
import { LoginScreen } from '@cortex/feature-client-management/ui/admin/screens/LoginScreen';
import { PmConversationScreen } from '@cortex/feature-client-management/ui/admin/screens/PmConversationScreen';
import { TenantDetailScreen } from '@cortex/feature-client-management/ui/admin/screens/TenantDetailScreen';
import { ExecutionsScreen } from '@cortex/feature-execution-history/ui/admin/screens/ExecutionsScreen';
import { type PmGroupKind, adminAuthStatus } from '@cortex/web-api/clients/admin/api';
import { type ReactElement, useEffect, useState } from 'react';

/**
 * Top-level state machine for the admin SPA.
 *
 *   unauthenticated  → LoginScreen (username/password)
 *   authenticated    → fleet | clients | executions | onboard | tenant-detail | pm-conversation
 *
 * No router. One auth flag, one nav enum, one selectedSlug,
 * one selectedChatId — keeps the surface honest. Session expiration kicks
 * back to LoginScreen with a small notice.
 *
 * tenant-detail is a per-slug drilldown (PR1 of v1.2 PM-onboarding) — clicking
 * a card on ClientsScreen sets selectedSlug + screen. Back button restores
 * 'clients'. selectedSlug is cleared on screen change away from tenant-detail.
 *
 * pm-conversation (PR7) is a drill-down INSIDE tenant-detail: clicking a PM
 * row sets selectedChatId. Back returns to tenant-detail with the same slug
 * still selected.
 */

type AuthState = 'unauthenticated' | 'authenticated';
type Screen = 'fleet' | 'clients' | 'executions' | 'onboard' | 'tenant-detail' | 'pm-conversation';

export function App() {
  const [authState, setAuthState] = useState<AuthState>('unauthenticated');
  const [screen, setScreen] = useState<Screen>('clients');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [selectedGroupKind, setSelectedGroupKind] = useState<PmGroupKind | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    adminAuthStatus().then((s) => {
      if (cancelled || !s.authenticated) return;
      setAuthState('authenticated');
      setScreen('clients');
      setNotice(null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (authState !== 'authenticated') {
    return (
      <LoginScreen
        onVerified={() => {
          setAuthState('authenticated');
          setScreen('clients');
          setSelectedSlug(null);
          setNotice(null);
        }}
        notice={notice}
      />
    );
  }

  const sessionExpired = () => {
    setSelectedSlug(null);
    setSelectedChatId(null);
    setSelectedGroupKind(null);
    setAuthState('unauthenticated');
    setNotice('Session expired. Sign in again.');
  };
  const loggedOut = () => {
    setSelectedSlug(null);
    setSelectedChatId(null);
    setSelectedGroupKind(null);
    setAuthState('unauthenticated');
    setNotice(null);
  };

  let body: ReactElement;
  if (screen === 'onboard') {
    body = <FormOnboardingScreen onCancel={() => setScreen('clients')} />;
  } else if (screen === 'pm-conversation' && selectedSlug && selectedChatId) {
    body = (
      <PmConversationScreen
        slug={selectedSlug}
        chatId={selectedChatId}
        groupKind={selectedGroupKind}
        onSessionExpired={sessionExpired}
        onBack={() => {
          setSelectedChatId(null);
          setSelectedGroupKind(null);
          setScreen('tenant-detail');
        }}
      />
    );
  } else if (screen === 'tenant-detail' && selectedSlug) {
    body = (
      <TenantDetailScreen
        slug={selectedSlug}
        onSessionExpired={sessionExpired}
        onBack={() => {
          setSelectedSlug(null);
          setSelectedGroupKind(null);
          setScreen('clients');
        }}
        onViewPmConversation={(chatId, groupKind = null) => {
          setSelectedChatId(chatId);
          setSelectedGroupKind(groupKind);
          setScreen('pm-conversation');
        }}
      />
    );
  } else if (screen === 'clients') {
    body = (
      <ClientsScreen
        onSessionExpired={sessionExpired}
        onLoggedOut={loggedOut}
        onAddNewClient={() => setScreen('onboard')}
        onSwitchToFleet={() => setScreen('fleet')}
        onSwitchToExecutions={() => setScreen('executions')}
        onSelectTenant={(slug) => {
          setSelectedSlug(slug);
          setSelectedGroupKind(null);
          setScreen('tenant-detail');
        }}
      />
    );
  } else if (screen === 'executions') {
    body = (
      <ExecutionsScreen
        onSessionExpired={sessionExpired}
        onLoggedOut={loggedOut}
        onSwitchToClients={() => setScreen('clients')}
        onSwitchToFleet={() => setScreen('fleet')}
      />
    );
  } else {
    body = (
      <FleetScreen
        onSessionExpired={sessionExpired}
        onLoggedOut={loggedOut}
        onSwitchToClients={() => setScreen('clients')}
        onSwitchToExecutions={() => setScreen('executions')}
      />
    );
  }

  return body;
}
